import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
    ExternalLink,
    Loader2,
    MessageSquare,
    RefreshCw,
    Search,
    Send,
    User,
} from "lucide-react";
import { toast } from "sonner";
import { muvekkilMerkez } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { PanelCard, SectionIntro } from "./ui";

function formatSaat(iso) {
    if (!iso) return "";
    try {
        return new Date(iso).toLocaleString("tr-TR", {
            day: "2-digit",
            month: "2-digit",
            hour: "2-digit",
            minute: "2-digit",
        });
    } catch {
        return "";
    }
}

function formatSaatKisa(iso) {
    if (!iso) return "";
    try {
        return new Date(iso).toLocaleTimeString("tr-TR", {
            hour: "2-digit",
            minute: "2-digit",
        });
    } catch {
        return "";
    }
}

function avatarHarf(ad) {
    const p = (ad || "?").trim().split(/\s+/);
    return ((p[0]?.[0] || "") + (p[1]?.[0] || "")).toUpperCase() || "?";
}

export default function MesajPanel({ onRefreshOzet }) {
    const [mod, setMod] = useState("tumu");
    const [q, setQ] = useState("");
    const [qDebounced, setQDebounced] = useState("");
    const [sohbetler, setSohbetler] = useState([]);
    const [seciliDosyaId, setSeciliDosyaId] = useState("");
    const [dosyaInfo, setDosyaInfo] = useState(null);
    const [mesajlar, setMesajlar] = useState([]);
    const [yanitMetin, setYanitMetin] = useState("");
    const [loadingList, setLoadingList] = useState(true);
    const [loadingChat, setLoadingChat] = useState(false);
    const [busySend, setBusySend] = useState(false);
    const threadRef = useRef(null);

    useEffect(() => {
        const t = setTimeout(() => setQDebounced(q.trim()), 300);
        return () => clearTimeout(t);
    }, [q]);

    const loadSohbetler = useCallback(async () => {
        setLoadingList(true);
        try {
            const data = await muvekkilMerkez.mesajSohbetler({
                mod,
                q: qDebounced || undefined,
                limit: 100,
            });
            const rows = data.sohbetler || [];
            setSohbetler(rows);
            setSeciliDosyaId((prev) => {
                if (prev && rows.some((r) => String(r.dosya_id) === String(prev))) return prev;
                return rows[0] ? String(rows[0].dosya_id) : "";
            });
        } catch {
            toast.error("Sohbet listesi yüklenemedi.");
        } finally {
            setLoadingList(false);
        }
    }, [mod, qDebounced]);

    const loadThread = useCallback(async (dosyaId) => {
        if (!dosyaId) {
            setDosyaInfo(null);
            setMesajlar([]);
            return;
        }
        setLoadingChat(true);
        try {
            const data = await muvekkilMerkez.mesajSohbet(dosyaId);
            setDosyaInfo(data.dosya || null);
            setMesajlar(data.mesajlar || []);
        } catch {
            toast.error("Mesaj geçmişi yüklenemedi.");
            setDosyaInfo(null);
            setMesajlar([]);
        } finally {
            setLoadingChat(false);
        }
    }, []);

    useEffect(() => {
        loadSohbetler();
    }, [loadSohbetler]);

    useEffect(() => {
        loadThread(seciliDosyaId);
    }, [seciliDosyaId, loadThread]);

    useEffect(() => {
        const el = threadRef.current;
        if (el) el.scrollTop = el.scrollHeight;
    }, [mesajlar, seciliDosyaId]);

    useEffect(() => {
        const timer = setInterval(() => {
            loadSohbetler().catch(() => {});
            if (seciliDosyaId) loadThread(seciliDosyaId).catch(() => {});
        }, 15000);
        return () => clearInterval(timer);
    }, [loadSohbetler, loadThread, seciliDosyaId]);

    const bekleyenSayisi = useMemo(
        () => sohbetler.filter((s) => s.bekleyen_yanit).length,
        [sohbetler],
    );

    async function handleGonder(e) {
        e.preventDefault();
        const metin = yanitMetin.trim();
        if (!seciliDosyaId || !metin) return;
        setBusySend(true);
        try {
            await muvekkilMerkez.mesajYanit({
                dosya_id: Number(seciliDosyaId),
                metin,
            });
            toast.success("Mesaj gönderildi — müvekkil panelinde görünür.");
            setYanitMetin("");
            await Promise.all([loadThread(seciliDosyaId), loadSohbetler()]);
            onRefreshOzet?.();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Gönderilemedi.");
        } finally {
            setBusySend(false);
        }
    }

    return (
        <PanelCard className="!p-0 overflow-hidden">
            <div className="px-4 pt-4 pb-3 border-b border-white/10">
                <SectionIntro
                    icon={MessageSquare}
                    title="Müvekkil mesajları"
                    description="Dosya bazlı portal yazışmaları — WhatsApp tarzı görünüm. Yanıtlar müvekkil panelinde Mesajlar sekmesine düşer."
                />
            </div>

            <div className="grid lg:grid-cols-[320px_1fr] min-h-[560px]">
                {/* Sol — sohbet listesi */}
                <div className="flex flex-col border-b lg:border-b-0 lg:border-r border-white/10 bg-[#0a111c]/80">
                    <div className="p-3 space-y-2 border-b border-white/10">
                        <div className="relative">
                            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-500" />
                            <input
                                value={q}
                                onChange={(e) => setQ(e.target.value)}
                                placeholder="Ad, T.C., plaka, dosya no…"
                                className="w-full rounded-lg bg-[#111827] border border-white/10 pl-8 pr-3 py-2 text-sm text-white placeholder:text-slate-500"
                            />
                        </div>
                        <div className="flex flex-wrap gap-1.5">
                            {[
                                { id: "bekleyen", label: `Yanıt bekleyen${bekleyenSayisi ? ` (${bekleyenSayisi})` : ""}` },
                                { id: "tumu", label: "Tüm sohbetler" },
                            ].map((f) => (
                                <button
                                    key={f.id}
                                    type="button"
                                    onClick={() => setMod(f.id)}
                                    className={`px-2.5 py-1 rounded-full text-[11px] border transition ${
                                        mod === f.id
                                            ? "border-[#25d366]/50 text-[#dcf8c6] bg-[#25d366]/15"
                                            : "border-white/10 text-slate-400 hover:text-white"
                                    }`}
                                >
                                    {f.label}
                                </button>
                            ))}
                            <Button
                                type="button"
                                size="sm"
                                variant="outline"
                                className="h-7 text-[10px] border-white/10 ml-auto"
                                onClick={() => {
                                    loadSohbetler();
                                    if (seciliDosyaId) loadThread(seciliDosyaId);
                                }}
                            >
                                <RefreshCw className="h-3 w-3" />
                            </Button>
                        </div>
                    </div>

                    <div className="flex-1 overflow-y-auto max-h-[480px] lg:max-h-none">
                        {loadingList ? (
                            <div className="flex items-center justify-center py-16 text-slate-500">
                                <Loader2 className="h-5 w-5 animate-spin mr-2" /> Yükleniyor…
                            </div>
                        ) : sohbetler.length === 0 ? (
                            <p className="text-sm text-slate-500 p-6 text-center">
                                {mod === "bekleyen" ? "Yanıt bekleyen sohbet yok." : "Henüz mesaj yok."}
                            </p>
                        ) : (
                            sohbetler.map((s) => {
                                const active = String(s.dosya_id) === String(seciliDosyaId);
                                return (
                                    <button
                                        key={s.dosya_id}
                                        type="button"
                                        onClick={() => setSeciliDosyaId(String(s.dosya_id))}
                                        className={`w-full text-left px-3 py-3 border-b border-white/5 hover:bg-white/[0.04] transition flex gap-3 ${
                                            active ? "bg-[#128c7e]/15 border-l-2 border-l-[#25d366]" : ""
                                        }`}
                                    >
                                        <span
                                            className={`shrink-0 w-11 h-11 rounded-full flex items-center justify-center text-sm font-bold ${
                                                s.bekleyen_yanit
                                                    ? "bg-[#8b1f2f] text-white"
                                                    : "bg-[#374151] text-slate-200"
                                            }`}
                                        >
                                            {avatarHarf(s.muvekkil_ad)}
                                        </span>
                                        <div className="min-w-0 flex-1">
                                            <div className="flex items-start justify-between gap-2">
                                                <div className="min-w-0">
                                                    <p className="text-sm font-semibold text-white truncate">
                                                        {s.muvekkil_ad || "Müvekkil"}
                                                    </p>
                                                    <p className="text-[10px] text-[#25d366] font-mono truncate">
                                                        T.C. {s.muvekkil_tc || "—"}
                                                    </p>
                                                </div>
                                                <span className="text-[10px] text-slate-500 shrink-0">
                                                    {formatSaatKisa(s.son_mesaj_tarihi)}
                                                </span>
                                            </div>
                                            <p className="text-[11px] text-slate-500 truncate mt-0.5">
                                                {s.dosya_no}
                                                {s.plaka ? ` · ${s.plaka}` : ""}
                                            </p>
                                            <p className="text-xs text-slate-400 truncate mt-1">
                                                {s.son_muvekkil_mi ? "👤 " : "🏢 "}
                                                {s.son_mesaj || "—"}
                                            </p>
                                        </div>
                                        {s.bekleyen_yanit ? (
                                            <span className="shrink-0 self-center min-w-[1.25rem] h-5 px-1.5 rounded-full bg-[#25d366] text-[10px] font-bold text-white flex items-center justify-center">
                                                !
                                            </span>
                                        ) : null}
                                    </button>
                                );
                            })
                        )}
                    </div>
                </div>

                {/* Sağ — sohbet */}
                <div
                    className="flex flex-col min-h-[420px] lg:min-h-[560px]"
                    style={{
                        backgroundImage:
                            "radial-gradient(circle at 20% 20%, rgba(18,140,126,0.06) 0%, transparent 50%), radial-gradient(circle at 80% 80%, rgba(139,31,47,0.05) 0%, transparent 45%)",
                    }}
                >
                    {!seciliDosyaId || !dosyaInfo ? (
                        <div className="flex-1 flex flex-col items-center justify-center text-slate-500 gap-3 p-8">
                            <MessageSquare className="h-12 w-12 opacity-30" />
                            <p className="text-sm">Soldan bir sohbet seçin</p>
                            <p className="text-xs text-slate-600 text-center max-w-xs">
                                Müvekkil adı, T.C. kimlik no ve dosya bilgileri burada görünür.
                            </p>
                        </div>
                    ) : (
                        <>
                            {/* Chat header */}
                            <div className="px-4 py-3 border-b border-white/10 bg-[#128c7e]/10 flex flex-wrap items-start justify-between gap-3">
                                <div className="flex gap-3 min-w-0">
                                    <span className="shrink-0 w-12 h-12 rounded-full bg-[#128c7e] text-white flex items-center justify-center font-bold">
                                        {avatarHarf(dosyaInfo.muvekkil_ad)}
                                    </span>
                                    <div className="min-w-0">
                                        <h3 className="text-base font-semibold text-white truncate">
                                            {dosyaInfo.muvekkil_ad || "Müvekkil"}
                                        </h3>
                                        <p className="text-xs text-[#dcf8c6] font-mono mt-0.5">
                                            T.C. {dosyaInfo.muvekkil_tc || "—"}
                                        </p>
                                        <p className="text-[11px] text-slate-400 mt-1">
                                            <User className="inline h-3 w-3 mr-1 opacity-70" />
                                            {dosyaInfo.muvekkil_telefon || "Telefon yok"}
                                            {dosyaInfo.muvekkil_email ? ` · ${dosyaInfo.muvekkil_email}` : ""}
                                        </p>
                                        <p className="text-[11px] text-[#D4AF37] mt-1">
                                            Dosya: {dosyaInfo.dosya_no}
                                            {dosyaInfo.plaka ? ` · ${dosyaInfo.plaka}` : ""}
                                            <span className="text-slate-500 ml-1">({dosyaInfo.dosya_durumu})</span>
                                        </p>
                                    </div>
                                </div>
                                <div className="flex flex-wrap gap-1.5">
                                    {dosyaInfo.bekleyen_yanit ? (
                                        <span className="text-[10px] px-2 py-1 rounded-full bg-amber-500/20 text-amber-200 border border-amber-500/30">
                                            Yanıt bekliyor
                                        </span>
                                    ) : null}
                                    <Link
                                        to={dosyaInfo.dosya_link}
                                        className="inline-flex items-center gap-1 text-[10px] px-2.5 py-1.5 rounded-md border border-white/10 text-slate-300 hover:text-white"
                                    >
                                        Dosya kartı
                                    </Link>
                                    <a
                                        href={dosyaInfo.portal_link}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="inline-flex items-center gap-1 text-[10px] px-2.5 py-1.5 rounded-md border border-[#25d366]/30 text-[#dcf8c6] hover:bg-[#25d366]/10"
                                    >
                                        <ExternalLink className="h-3 w-3" /> Müvekkil paneli
                                    </a>
                                </div>
                            </div>

                            {/* Messages */}
                            <div
                                ref={threadRef}
                                className="flex-1 overflow-y-auto p-4 space-y-2 min-h-[280px] max-h-[380px] lg:max-h-[420px]"
                            >
                                {loadingChat ? (
                                    <div className="flex justify-center py-12 text-slate-500">
                                        <Loader2 className="h-5 w-5 animate-spin" />
                                    </div>
                                ) : mesajlar.length === 0 ? (
                                    <p className="text-center text-sm text-slate-500 py-12">
                                        Bu dosyada henüz mesaj yok.
                                    </p>
                                ) : (
                                    mesajlar.map((m) => {
                                        const mine = m.staff_mi || !m.muvekkil_kaynakli;
                                        return (
                                            <div
                                                key={m.id}
                                                className={`flex ${mine ? "justify-end" : "justify-start"}`}
                                            >
                                                <div
                                                    className={`max-w-[min(85%,420px)] rounded-lg px-3 py-2 shadow-sm ${
                                                        mine
                                                            ? "bg-[#005c4b] border border-[#25d366]/25 text-[#e9edef] rounded-tr-none"
                                                            : "bg-[#202c33] border border-white/10 text-[#e9edef] rounded-tl-none"
                                                    }`}
                                                >
                                                    <div className="flex items-center justify-between gap-3 text-[10px] text-slate-400 mb-1">
                                                        <span className="font-medium">
                                                            {mine ? m.yazar || "MBS Hukuk" : "Müvekkil"}
                                                        </span>
                                                        <time>{formatSaat(m.olusturma_tarihi)}</time>
                                                    </div>
                                                    <p className="text-sm whitespace-pre-wrap leading-relaxed">
                                                        {m.metin}
                                                    </p>
                                                </div>
                                            </div>
                                        );
                                    })
                                )}
                            </div>

                            {/* Composer */}
                            <form
                                onSubmit={handleGonder}
                                className="p-3 border-t border-white/10 bg-[#0a111c]/90 flex gap-2 items-end"
                            >
                                <Textarea
                                    value={yanitMetin}
                                    onChange={(e) => setYanitMetin(e.target.value)}
                                    rows={2}
                                    maxLength={2000}
                                    placeholder="Mesajınızı yazın… (müvekkil panelinde görünür)"
                                    className="flex-1 bg-[#111827] border-white/10 text-white resize-none min-h-[44px]"
                                    onKeyDown={(e) => {
                                        if (e.key === "Enter" && !e.shiftKey) {
                                            e.preventDefault();
                                            handleGonder(e);
                                        }
                                    }}
                                />
                                <Button
                                    type="submit"
                                    disabled={busySend || !yanitMetin.trim()}
                                    className="shrink-0 bg-[#128c7e] hover:bg-[#0d6b5f] text-white h-11 px-4"
                                >
                                    {busySend ? (
                                        <Loader2 className="h-4 w-4 animate-spin" />
                                    ) : (
                                        <Send className="h-4 w-4" />
                                    )}
                                </Button>
                            </form>
                        </>
                    )}
                </div>
            </div>
        </PanelCard>
    );
}
